import type { Ratecard } from '@clockwise/clients/ratecards';
import {
  type BillingModel,
  type Engagement,
  type EngagementInput,
  type ReplaceSowInput,
  type Sow,
} from '@clockwise/clients/sows';
import {
  ApiError,
  INVOICE_CADENCE_LABELS,
  isInvoicingPausedOn,
  isoDateString,
} from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import { CustomFieldsViewer } from '@/components/custom/custom-fields-viewer';
import { DetailRow } from '@/components/custom/detail-list';
import { formatRoleLabel } from '@/components/custom/engagement-rate-table';
import { EngagementSheet } from '@/components/custom/engagement-sheet';
import { EngagementsSection } from '@/components/custom/engagements-section';
import { NoAccess } from '@/components/custom/no-access';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import type { ProductDisplayLookup } from '@/components/custom/product-label';
import { RequirePermission } from '@/components/custom/require-permission';
import { SowTypeBadge } from '@/components/custom/sow-type-badge';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUnsavedRouteGuard } from '@/hooks/use-unsaved-route-guard';
import {
  auditApi,
  customersApi,
  productsApi,
  ratecardsApi,
  serviceRolesApi,
  sowsApi,
} from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { useResolveCustomFieldLabel } from '@/lib/use-custom-field-definitions';
import { localTodayIso } from '@/lib/utils';

const TAB_VALUES = ['details', 'versions', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  asOf: isoDateString().optional(),
  tab: z.enum(TAB_VALUES).optional(),
  /** One-shot flag set by the "Add engagement" flow to land the user in amend
   *  mode (so they can immediately add lines on a fresh SOW). Consumed and
   *  stripped from the URL on first read — reloading the page after an
   *  amendment is canceled brings you back to read-only.
   *
   *  Accepts the number `1` as well as the string `"1"`: the router parses an
   *  unquoted `?amend=1` as a number, so a literal-`'1'`-only schema threw and
   *  hard-crashed the page on that URL. Both normalize to `'1'`; anything else
   *  falls back to "not amending" instead of erroring. */
  amend: z
    .union([z.literal('1'), z.literal(1)])
    .optional()
    .catch(undefined)
    .transform((v) => (v === undefined ? undefined : ('1' as const))),
});

export const Route = createFileRoute('/_authenticated/customers/$id/sows/$sowId')({
  validateSearch: detailSearch,
  component: SowDetailPage,
});

const DEFAULT_TAB: TabValue = 'details';
const AUDIT_PAGE_SIZE = 50;
// Rate cards are few per tenant; one page comfortably backs the picker.
const RATE_CARD_PICKER_LIMIT = 100;
// Radix Select forbids empty-string item values, so "no rate card" needs a
// sentinel that maps back to null on change.
const NO_RATE_CARD = '__none__';

function SowDetailPage() {
  const { id: customerId, sowId } = Route.useParams();
  const { asOf, tab, amend: amendFlag } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  // Fetch the version list first; without an as-of date we resolve to the
  // *latest* version (not the one in effect today). Otherwise a SOW that
  // expired or hasn't started yet would render as "not found" even though
  // the user clicked it from the list.
  const versions = useQuery({
    queryKey: queryKeys.sows.versions(sowId),
    queryFn: () => sowsApi.versions(sowId),
  });

  const latestVersion = versions.data?.data.reduce<number | null>(
    (acc, v) => (acc == null || v.version > acc ? v.version : acc),
    null,
  );

  const detail = useQuery({
    queryKey: queryKeys.sows.detail(sowId, asOf ?? `v:${latestVersion ?? ''}`),
    queryFn: () => {
      if (asOf) return sowsApi.get(sowId, asOf);
      if (latestVersion != null) return sowsApi.getVersion(sowId, latestVersion);
      throw new Error('No versions available');
    },
    enabled: Boolean(asOf) || latestVersion != null,
  });

  const auditResolveLabel = useResolveCustomFieldLabel('sow');
  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'sows',
      entityId: sowId,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({ entityType: 'sows', entityId: sowId, limit: AUDIT_PAGE_SIZE, offset: 0 }),
    enabled: canViewAudit,
  });

  const customerQuery = useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => customersApi.get(customerId),
  });

  const sow = detail.data?.data ?? null;
  const serverEngagements = detail.data?.related.engagements ?? [];
  // `createdAt` is part of the key so a same-day in-place amend — which keeps
  // the version number but rewrites the version's content (and refreshes
  // createdAt with it) — still re-syncs the draft when the refetched SOW
  // lands. Without it the resync effect wouldn't re-fire (id + version
  // unchanged) and the Engagements section would show stale data until a full
  // page reload.
  const serverEngagementsKey = `${sow?.id ?? ''}@${sow?.version ?? ''}@${sow?.createdAt ?? ''}`;

  // ---- Amendment state ----------------------------------------------------
  // The SOW page is read-only by default. Clicking "Amend" enters edit mode
  // where every material field (name, document #, dates, signed, lines)
  // becomes editable. Changes stage locally; "Save amendment" issues a
  // single PUT that creates a new SOW version.
  //
  // `amending` is DERIVED from the URL flag (`?amend=1`), not stored as
  // local state. This makes it survive React StrictMode dev double-invokes
  // (which unmount+remount and would otherwise reset useState back to
  // false), and means refresh/back-button preserve the mode. Toggling is
  // done by navigating to add/remove the flag — see startAmend / cancel /
  // mutation onSuccess below.
  const amending = amendFlag === '1';
  // The amendment form (header + engagements) lives in react-hook-form so its
  // dirty state powers the unsaved-changes guard — including rate edits staged
  // from the engagement sheet, which write into `engagements` via setValue.
  // The read-only section also renders from `engagements`.
  const form = useForm<AmendForm>({ defaultValues: EMPTY_AMEND_FORM });
  const engagements = form.watch('engagements');
  // Whether the operator has touched the rate-card picker this amend session.
  // `rateCardId === null` is ambiguous — it means both "explicitly None" and
  // "not pinned / not yet defaulted" — so this flag is the authority on intent:
  // once set, the auto-default never overwrites the choice (a clear to None
  // included), and rate prefill treats null as a real "no card".
  const [rateCardTouched, setRateCardTouched] = useState(false);
  // Set true on a successful save so the post-save navigation (which removes
  // ?amend while the form is briefly still dirty) isn't caught by the guard.
  const savedRef = useRef(false);
  // Sheet state — null when closed.
  // Identified by INDEX rather than by id: an engagement added in this amend
  // session has no id until the server mints one, so position in the staged
  // list is the only handle that exists for every row.
  const [sheetState, setSheetState] = useState<{
    mode: 'add' | 'edit';
    index: number | null;
    engagement: EngagementInput;
  } | null>(null);

  useEffect(() => {
    // Resync the form when the loaded SOW version changes OR the user enters/
    // exits amend mode: seed header + lines from the server and clear dirty.
    // `lines` is seeded in read-only mode too, since the Lines section renders
    // from it.
    form.reset(
      sow
        ? { ...headerFromSow(sow), engagements: serverEngagements.map(engagementToInput) }
        : EMPTY_AMEND_FORM,
    );
    setSheetState(null);
    setRateCardTouched(false);
    savedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverEngagementsKey, amending]);

  // The operator picks which rate card a SOW draws from, defaulting to the
  // tenant's current one. T&M service rates prefill from the *selected* card,
  // not always the in-effect one. `ratecardQuery` resolves the current card
  // (used only for the default); `ratecardsListQuery` backs the picker and
  // supplies the selected card's lines for the rate prefill.
  const ratecardQuery = useQuery({
    queryKey: queryKeys.ratecards.active(),
    queryFn: () => ratecardsApi.getActive(),
  });
  const ratecardsListQuery = useQuery({
    queryKey: queryKeys.ratecards.list({
      limit: RATE_CARD_PICKER_LIMIT,
      sortField: 'effectiveFrom',
      sortDirection: 'desc',
    }),
    queryFn: () =>
      ratecardsApi.list({
        limit: RATE_CARD_PICKER_LIMIT,
        sortField: 'effectiveFrom',
        sortDirection: 'desc',
      }),
  });
  // The picker list is windowed (RATE_CARD_PICKER_LIMIT, newest first), so a SOW
  // pinned to an older card can fall outside it — leaving its label as "—" and
  // its rates unresolved. Fetch that card by id and fold it into the list so it
  // always resolves. Only fires when the pinned card isn't already in the page.
  const pinnedRateCardId = sow?.rateCardId ?? null;
  const pinnedInList =
    pinnedRateCardId != null &&
    (ratecardsListQuery.data?.data ?? []).some((c) => c.id === pinnedRateCardId);
  const pinnedRateCardQuery = useQuery({
    queryKey: queryKeys.ratecards.detail(pinnedRateCardId ?? ''),
    queryFn: () => ratecardsApi.get(pinnedRateCardId ?? ''),
    enabled: pinnedRateCardId != null && !pinnedInList,
  });
  const rateCards = useMemo(() => {
    const list = ratecardsListQuery.data?.data ?? [];
    const pinned = pinnedRateCardQuery.data?.data ?? null;
    return pinned && !list.some((c) => c.id === pinned.id) ? [pinned, ...list] : list;
  }, [ratecardsListQuery.data, pinnedRateCardQuery.data]);

  // While amending, rates follow the draft's selection; in read-only mode they
  // follow the card the SOW is pinned to. An unpinned draft the operator hasn't
  // touched falls back to the current card so prefill stays useful — but once
  // they touch the picker, a null selection means "no card" (no prefill), not
  // "default to current".
  const activeRateCardId = ratecardQuery.data?.data?.id ?? null;
  const draftRateCardId = form.watch('rateCardId');
  const selectedRateCardId = amending
    ? (draftRateCardId ?? (rateCardTouched ? null : activeRateCardId))
    : (sow?.rateCardId ?? null);

  const selectedRateCard = useMemo(() => {
    const active = ratecardQuery.data?.data ?? null;
    // Resolve from the (pinned-augmented) list, falling back to the separately
    // fetched active card when it's the selection but sits outside the window.
    return (
      rateCards.find((c) => c.id === selectedRateCardId) ??
      (active && active.id === selectedRateCardId ? active : null)
    );
  }, [rateCards, ratecardQuery.data, selectedRateCardId]);

  const rateCardRates = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of selectedRateCard?.lines ?? []) m.set(l.serviceRoleId, l.rate);
    return m;
  }, [selectedRateCard]);

  // ---- Reference data for cards + sheets (products, roles) ----------------
  // Resolved once at the page level so the retainer's activity list, the T&M
  // role names, and the rate-card rows all share the same data fetch. Sits
  // below the rate-card block because the role set is derived from the
  // selected card's lines as well as the SOW's own.
  const referencedProductIds = useMemo(() => {
    const ids = new Set<string>();
    // Activities live on EVERY billing model now, not just retainers.
    for (const e of engagements) for (const a of e.activities) ids.add(a.productId);
    return [...ids].sort();
  }, [engagements]);

  const productsQuery = useQuery({
    queryKey: queryKeys.products.byIds(referencedProductIds),
    queryFn: () => productsApi.findByIds(referencedProductIds),
    enabled: referencedProductIds.length > 0,
  });

  // Resolve only the roles this SOW actually references — the roles on its
  // T&M services, the primary role of each retainer product, and every role
  // priced by the selected rate card — via a batched by-id fetch. This
  // replaces a capped `list({ limit: 500 })` that silently dropped any role
  // past the cap (and 400'd outright above it). Bounded by references, so it
  // can't truncate regardless of tenant size.
  //
  // T&M service roleIds come straight from the draft, so the query fires
  // immediately and resolves those names IN PARALLEL with `productsQuery`.
  // Retainer-product roles can only be added once products resolve (we need
  // each product's `serviceRoleId`), so the set widens then — the one
  // unavoidable hop, and only for retainer chips.
  const referencedRoleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of engagements) {
      if (e.billingModel === 'tm') for (const r of e.rates) ids.add(r.roleId);
      // A retainer's overage rates name roles nothing else on the page does.
      if (e.billingModel === 'retainer') for (const r of e.overageRates ?? []) ids.add(r.roleId);
    }
    // A SOW with no T&M line renders the selected rate card's rows instead
    // (EngagementsSection's "Global" card). Those roles are referenced by nothing
    // else on the page, so without this they'd render as raw ids — only the
    // handful that happen to double as a retainer product's role resolved.
    //
    // Gated on the SAME condition that decides whether those rows render. Add
    // them unconditionally and a T&M-bearing SOW widens this set once the rate
    // card lands — re-keying `rolesQuery` after it already resolved, which
    // (no placeholderData) empties `roleLookup` and flashes every T&M row back
    // to the raw-id fallback while a fetch nothing renders is in flight.
    if (!engagements.some((e) => e.billingModel === 'tm')) {
      for (const l of selectedRateCard?.lines ?? []) ids.add(l.serviceRoleId);
    }
    // An engagement being added/edited in the sheet may reference roles that
    // aren't in the saved draft yet — e.g. a new T&M seeded from the rate
    // card. Resolve their names too so the sheet shows names, not raw ids.
    const drafted = sheetState?.engagement;
    if (drafted?.billingModel === 'tm') for (const r of drafted.rates) ids.add(r.roleId);
    if (drafted?.billingModel === 'retainer')
      for (const r of drafted.overageRates ?? []) ids.add(r.roleId);
    for (const p of productsQuery.data?.data ?? []) ids.add(p.serviceRoleId);
    return [...ids].sort();
  }, [engagements, productsQuery.data, sheetState, selectedRateCard]);

  const rolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.byIds(referencedRoleIds),
    queryFn: () => serviceRolesApi.findByIds(referencedRoleIds),
    enabled: referencedRoleIds.length > 0,
  });
  // Every active, customer-facing (non-internal) service role. A new T&M line
  // defaults to selecting them all, and these rows carry names so the seeded
  // services render names immediately — no extra by-id round-trip. Internal
  // roles are non-billable, so they're excluded. Only needed while amending,
  // where lines can be added. Capped like the bulk-add picker — a tenant with
  // >200 such roles seeds the first page, then adds the rest via the picker.
  const activeRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      internal: false,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 200,
      offset: 0,
    }),
    queryFn: () =>
      serviceRolesApi.list({
        active: true,
        internal: false,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 200,
        offset: 0,
      }),
    enabled: amending,
  });
  const roleLookup = useMemo(() => {
    const m = new Map<string, string>();
    // Active roles first (they carry labels for seeded rows); the by-id fetch
    // fills in any referenced role not in that set — e.g. a since-deactivated
    // or internal role on a saved line, or a retainer product's role. Roles
    // render as "CODE · Name".
    for (const r of activeRolesQuery.data?.data ?? [])
      m.set(r.id, formatRoleLabel(r.shortCode, r.name));
    for (const r of rolesQuery.data?.data ?? []) m.set(r.id, formatRoleLabel(r.shortCode, r.name));
    return (id: string) => m.get(id) ?? `Role ${id.slice(0, 8)}…`;
  }, [rolesQuery.data, activeRolesQuery.data]);

  // Rich product lookup: name + primary-role short code, so consumers can
  // render the badge-prefixed label via ProductLabel. Three states for
  // `serviceRoleCode`:
  //
  //   string    — resolved; chip shown.
  //   null      — resolved but the role isn't in our catalog (rare; the
  //               by-id fetch only misses a role that was deleted out from
  //               under the reference); chip omitted.
  //   undefined — role data still loading. ProductLabel renders a skeleton
  //               chip so the row doesn't flicker from chipless to chipped
  //               when rolesQuery resolves a few frames after products.
  //
  // The Map updates live in refs and the returned function is a stable
  // useCallback — a future React.memo around any consumer (virtualized SOW
  // tables, etc.) won't churn on every TanStack Query refetch.
  const productsMapRef = useRef(new Map<string, { name: string; serviceRoleId: string }>());
  const roleCodesMapRef = useRef(new Map<string, string>());
  productsMapRef.current = useMemo(() => {
    const m = new Map<string, { name: string; serviceRoleId: string }>();
    for (const p of productsQuery.data?.data ?? []) {
      m.set(p.id, { name: p.name, serviceRoleId: p.serviceRoleId });
    }
    return m;
  }, [productsQuery.data]);
  roleCodesMapRef.current = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rolesQuery.data?.data ?? []) {
      // Defensive: skip rows whose shortCode is empty. The read schema now
      // requires .min(1), but a legacy row could still slip through.
      if (r.shortCode) m.set(r.id, r.shortCode);
    }
    return m;
  }, [rolesQuery.data]);
  // Treat the query as "settled" once it's no longer pending — whether it
  // succeeded OR errored. The previous `data !== undefined` check left
  // chips skeleton-animating indefinitely whenever /api/service-roles
  // returned 403/500/etc., because `data` stays undefined on error too.
  // On error we now fall through to `serviceRoleCode: null` (no chip),
  // which is a clear visual state instead of a perpetual loading shimmer.
  // No referenced roles → the by-id query is disabled and never leaves
  // "pending"; treat that as loaded so chips don't shimmer indefinitely.
  // Also keep chips in the loading state while products are still resolving:
  // a retainer line's role can't even be requested until its product lands,
  // so without this a retainer-only SOW would flash "no chip" (empty role
  // set → loaded) before the widened query fires.
  const rolesLoaded =
    (referencedRoleIds.length === 0 || rolesQuery.status !== 'pending') &&
    (referencedProductIds.length === 0 || productsQuery.status !== 'pending');
  const productLookup = useCallback<ProductDisplayLookup>(
    (source) => {
      // Resolve from either an id or a directly-supplied product source.
      // The second path is what lets the retainer sheet render newly-
      // picked products immediately — they're not yet in the page's
      // referencedProductIds set, so productsMapRef doesn't have them.
      const p =
        typeof source === 'string'
          ? productsMapRef.current.get(source)
          : { name: source.name, serviceRoleId: source.serviceRoleId };
      if (!p) {
        return {
          name: `Activity ${(source as string).slice(0, 8)}…`,
          // `undefined` (loading) until products settle, so a not-yet-fetched
          // product isn't mistaken for one that doesn't exist.
          serviceRoleCode: rolesLoaded ? null : undefined,
        };
      }
      // `undefined` while rolesQuery is in flight → skeleton chip.
      const code = rolesLoaded ? (roleCodesMapRef.current.get(p.serviceRoleId) ?? null) : undefined;
      return { name: p.name, serviceRoleCode: code, serviceRoleId: p.serviceRoleId };
    },
    [rolesLoaded],
  );

  // Default the picker to the tenant's current rate card when amending a SOW
  // that hasn't pinned one. Skipped once the operator has touched the picker,
  // so it never overwrites a manual pick or a deliberate clear to None.
  useEffect(() => {
    if (!amending || rateCardTouched) return;
    if (sow?.rateCardId != null) return;
    if (activeRateCardId == null) return;
    // Auto-default only; NOT shouldDirty, so merely entering amend on an
    // unpinned SOW doesn't trip the unsaved-changes guard. The defaulted card
    // is still persisted on Save (saveAmendment reads the live form value).
    if (form.getValues('rateCardId') == null) {
      form.setValue('rateCardId', activeRateCardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amending, rateCardTouched, sow?.rateCardId, activeRateCardId]);

  // ---- Amend mutation -----------------------------------------------------
  const amendMutation = useMutation({
    mutationFn: (input: AmendForm) => {
      if (!sow) throw new Error('SOW not loaded');
      const payload: ReplaceSowInput = {
        expectedVersion: sow.version,
        customerId: sow.customerId,
        rateCardId: input.rateCardId,
        rateCardVersion: sow.rateCardVersion,
        name: input.name,
        documentNo: input.documentNo,
        signedAt: input.signedAt,
        signedBy: input.signedBy,
        effectiveFrom: input.effectiveFrom,
        effectiveTo: input.effectiveTo,
        invoicingPauseFrom: input.invoicingPauseFrom,
        invoicingPauseUntil: input.invoicingPauseUntil,
        engagements: input.engagements,
      };
      return sowsApi.replace(sow.id, payload);
    },
    onSuccess: () => {
      toast.success('Amendment saved');
      queryClient.invalidateQueries({ queryKey: queryKeys.sows.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      // Let the post-save navigation through the unsaved-changes guard (the
      // form is still dirty at this instant; the resync effect clears it once
      // the new version lands).
      savedRef.current = true;
      // Drop ?amend=1 so the page exits amend mode. The resync effect picks
      // up the new SOW version and reseeds the form.
      navigate({ search: (s) => ({ ...s, amend: undefined }), replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  // ---- Engagement sheet handlers ------------------------------------------

  // A new engagement leaves its own term NULL, which means "the SOW's window"
  // — so the sheet needs no copy of the staged effective dates. The server
  // validates any term the operator does set against the SOW being written,
  // including one edited in the same amendment.

  /**
   * A blank engagement of one billing model.
   *
   * A new T&M engagement defaults to every active, customer-facing service
   * role, rated from the SOW's selected rate card (0 when the card doesn't
   * price it). `baseRate` snapshots that card rate so later edits read as
   * overrides; `rate` starts equal (not customized). The operator then drops
   * roles or overrides rates. Roles not yet loaded → an empty list, and the
   * picker adds them.
   */
  const blankEngagement = (model: BillingModel): EngagementInput => {
    const common = {
      name: '',
      description: null,
      invoiceStart: null,
      invoiceEnd: null,
      billingCadence: null,
      consolidate: null,
      manual: false,
      activities: [],
    };
    if (model === 'tm') {
      const rates = (activeRolesQuery.data?.data ?? []).map((r) => {
        const base = rateCardRates.get(r.id) ?? 0;
        return { roleId: r.id, rate: base, baseRate: base };
      });
      return { ...common, billingModel: 'tm', rates };
    }
    if (model === 'metrics') {
      return {
        ...common,
        billingModel: 'metrics',
        metricName: '',
        tiers: [{ fromQty: 0, toQty: null, price: 0 }],
      };
    }
    return { ...common, billingModel: 'retainer', fee: 0, discount: null, overageRates: null };
  };

  const openAdd = (model: BillingModel) => {
    if (!sow) return;
    setSheetState({ mode: 'add', index: null, engagement: blankEngagement(model) });
  };

  const openEdit = (engagement: EngagementInput, index: number) => {
    setSheetState({ mode: 'edit', index, engagement });
  };

  // All engagement mutations write through the form (shouldDirty) so staged
  // changes — including rate edits from the sheet — arm the unsaved guard.
  const setEngagements = (next: EngagementInput[]) =>
    form.setValue('engagements', next, { shouldDirty: true });

  const deleteEngagementAt = (index: number) => {
    setEngagements(form.getValues('engagements').filter((_, i) => i !== index));
    // If the sheet is editing the row just deleted, close it — otherwise its
    // local draft still holds the engagement and Save would re-add it. Rows
    // after the deleted one shift down, so an open sheet pointing past it is
    // now editing a different engagement; closing is the honest response.
    if (sheetState?.index != null && sheetState.index >= index) setSheetState(null);
  };

  const handleSheetSave = (engagement: EngagementInput) => {
    const prev = form.getValues('engagements');
    const index = sheetState?.index;
    setEngagements(
      index == null ? [...prev, engagement] : prev.map((e, i) => (i === index ? engagement : e)),
    );
  };

  const handleSheetDelete = () => {
    if (sheetState?.index == null) return;
    deleteEngagementAt(sheetState.index);
    setSheetState(null);
  };

  const startAmend = () => {
    if (!sow) return;
    navigate({ search: (s) => ({ ...s, amend: '1' as const }), replace: true });
  };
  const saveAmendment = () => {
    amendMutation.mutate(form.getValues());
  };
  const cancelAmendment = () => {
    navigate({ search: (s) => ({ ...s, amend: undefined }), replace: true });
  };

  // Block leaving the page (a different pathname) with unsaved amendment edits
  // — header fields AND T&M changes staged from the line sheet both mark the
  // form dirty. Same-route navigations (tab switch, Save/Cancel removing
  // ?amend) are allowed through. `enableBeforeUnload` arms the native prompt
  // for tab-close/refresh, gated so it only fires when there's real unsaved
  // work. Read live so the post-save redirect (savedRef) passes.
  // Read isDirty during render so the RHF proxy subscribes and the guard
  // closures below are recreated whenever dirtiness flips.
  const isDirty = form.formState.isDirty;
  const guardActive = () => amending && isDirty && !savedRef.current;
  const { blockerDialog } = useUnsavedRouteGuard(
    ({ current, next }) => guardActive() && next.pathname !== current.pathname,
    {
      title: 'Discard unsaved amendment?',
      description:
        'Your SOW amendment — including any rate changes — hasn’t been saved. Leaving will discard it.',
    },
    guardActive,
  );

  // What "inherit" resolves to, shown on the cards and in the sheet's pickers.
  // NULL on an engagement means "ask the level above", and a picker that
  // rendered that as an empty field would make a decision someone made at the
  // customer or SOW level look unset.
  const customer = customerQuery.data?.data;
  const customerCadenceLabel = customer?.invoiceCadence
    ? (INVOICE_CADENCE_LABELS[customer.invoiceCadence] ?? customer.invoiceCadence)
    : 'not set';

  const customerName = customerQuery.data?.data.name;

  // 403 → this SOW's customer isn't in the caller's assigned set.
  if (
    (versions.isError && isForbidden(versions.error)) ||
    (detail.isError && isForbidden(detail.error))
  ) {
    return (
      <PageShell>
        <NoAccess permission="customer.sow.view" resource="this engagement" />
      </PageShell>
    );
  }

  return (
    <PageShell className="gap-6 overflow-y-auto">
      {/* A SOW is always read in the customer context — the back link names
          the customer, the same `BackLink` the customer page itself uses. */}
      <div>
        <BackLink to="/customers/$id" params={{ id: customerId }}>
          Back to {customerName ?? 'customer'}
        </BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading || (versions.isLoading && !sow) ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : sow ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">
                  v{sow.version} · {sow.name}
                </h1>
                <SowTypeBadge type={sow.sowType ?? null} />
              </div>
              <p className="text-sm text-muted-foreground">
                {sow.documentNo ? (
                  <>
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">{sow.documentNo}</code> ·{' '}
                  </>
                ) : null}
                Effective {sow.effectiveFrom}
                {sow.effectiveTo ? ` – ${sow.effectiveTo}` : ' (open)'}
              </p>
            </>
          ) : versions.data?.data.length === 0 ? (
            <h1 className="text-2xl font-semibold text-destructive">Engagement not found.</h1>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          {/* Disabled while amending — changing as-of would resync the loaded
              SOW and silently discard the in-flight draft. Users must save or
              cancel the amendment first. */}
          <label
            htmlFor="as-of-date"
            className="flex items-center gap-2 text-xs text-muted-foreground"
            title={amending ? 'Save or cancel the amendment to change the as-of date.' : undefined}
          >
            As of
            <DatePicker
              id="as-of-date"
              value={asOf ?? ''}
              onChange={(v) =>
                navigate({ search: (s) => ({ ...s, asOf: v || undefined }), replace: true })
              }
              placeholder="Latest"
              clearable
              disabled={amending}
              className="h-9 w-44"
            />
          </label>
          <RequirePermission permission="customer.sow.edit">
            {sow ? (
              amending ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelAmendment}
                    disabled={amendMutation.isPending}
                  >
                    Cancel amendment
                  </Button>
                  <Button size="sm" onClick={saveAmendment} disabled={amendMutation.isPending}>
                    {amendMutation.isPending ? 'Saving…' : 'Save amendment'}
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={startAmend}>
                  <Pencil className="size-4" /> Amend
                </Button>
              )
            ) : null}
          </RequirePermission>
        </div>
      </header>

      {/* Card-wrapped pill tabs, matching the customer and employee pages. */}
      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="shrink-0 rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          <PillTabsTrigger value="versions">
            Versions
            {versions.data ? (
              <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground">
                {versions.data.data.length}
              </span>
            ) : null}
          </PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="rounded-md border">
            {detail.isLoading ? (
              <DetailsSkeleton />
            ) : sow ? (
              amending ? (
                <DetailsEditor
                  draft={{
                    name: form.watch('name'),
                    documentNo: form.watch('documentNo'),
                    rateCardId: form.watch('rateCardId'),
                    effectiveFrom: form.watch('effectiveFrom'),
                    effectiveTo: form.watch('effectiveTo'),
                    invoicingPauseFrom: form.watch('invoicingPauseFrom'),
                    invoicingPauseUntil: form.watch('invoicingPauseUntil'),
                    signedAt: form.watch('signedAt'),
                    signedBy: form.watch('signedBy'),
                  }}
                  onChange={(next) => {
                    form.setValue('name', next.name, { shouldDirty: true });
                    form.setValue('documentNo', next.documentNo, { shouldDirty: true });
                    form.setValue('effectiveFrom', next.effectiveFrom, { shouldDirty: true });
                    form.setValue('effectiveTo', next.effectiveTo, { shouldDirty: true });
                    form.setValue('invoicingPauseFrom', next.invoicingPauseFrom, {
                      shouldDirty: true,
                    });
                    form.setValue('invoicingPauseUntil', next.invoicingPauseUntil, {
                      shouldDirty: true,
                    });
                    form.setValue('signedAt', next.signedAt, { shouldDirty: true });
                    form.setValue('signedBy', next.signedBy, { shouldDirty: true });
                  }}
                  onRateCardChange={(id) => {
                    setRateCardTouched(true);
                    form.setValue('rateCardId', id, { shouldDirty: true });
                  }}
                  disabled={amendMutation.isPending}
                  sow={sow}
                  rateCards={rateCards}
                />
              ) : (
                <DetailsTable sow={sow} rateCards={rateCards} />
              )
            ) : (
              <p className="p-4 text-sm text-muted-foreground">
                No version was in effect on the selected date.
              </p>
            )}
          </div>
          {sow ? <SowCustomFieldsPanel sow={sow} /> : null}
        </PillTabsContent>

        <PillTabsContent value="versions" className="space-y-3">
          <div className="rounded-md border">
            {versions.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : versions.isError ? (
              <p className="p-4 text-sm text-destructive">Failed to load versions.</p>
            ) : (
              <VersionsTable versions={versions.data?.data ?? []} currentVersion={sow?.version} />
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList
                items={audit.data?.data ?? []}
                total={audit.data?.meta.total ?? 0}
                resolveLabel={auditResolveLabel}
              />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      {/* Engagements are not a tab — they're the working surface of a SOW,
       * always visible. */}
      <section className="space-y-3 rounded-xl border bg-card p-4">
        <h2 className="text-sm font-semibold tracking-tight">
          Engagements
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {engagements.length}
          </span>
        </h2>
        {detail.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : sow ? (
          <EngagementsSection
            engagements={engagements}
            amending={amending}
            productLookup={productLookup}
            roleLookup={roleLookup}
            rateCard={selectedRateCard}
            customerCadenceLabel={customerCadenceLabel}
            onEngagementClick={openEdit}
            onEngagementDelete={deleteEngagementAt}
            onAdd={openAdd}
          />
        ) : (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            No SOW version loaded.
          </p>
        )}
      </section>

      {sow ? (
        <EngagementSheet
          open={sheetState != null}
          mode={sheetState?.mode ?? 'edit'}
          value={sheetState?.engagement ?? null}
          customerCadenceLabel={customerCadenceLabel}
          productLookup={productLookup}
          roleLookup={roleLookup}
          onOpenChange={(open) => {
            if (!open) setSheetState(null);
          }}
          onSave={handleSheetSave}
          onDelete={sheetState?.mode === 'edit' ? handleSheetDelete : undefined}
          onModelChange={blankEngagement}
        />
      ) : null}
      {blockerDialog}
    </PageShell>
  );
}

interface HeaderDraft {
  name: string;
  documentNo: string | null;
  rateCardId: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  invoicingPauseFrom: string | null;
  invoicingPauseUntil: string | null;
  signedAt: string | null;
  signedBy: string | null;
}

/** The amendment form: SOW header fields + its lines. Held in react-hook-form
 *  so `formState.isDirty` drives the unsaved-changes guard. */
interface AmendForm extends HeaderDraft {
  engagements: EngagementInput[];
}

const EMPTY_AMEND_FORM: AmendForm = {
  name: '',
  documentNo: null,
  rateCardId: null,
  effectiveFrom: '',
  effectiveTo: null,
  invoicingPauseFrom: null,
  invoicingPauseUntil: null,
  signedAt: null,
  signedBy: null,
  engagements: [],
};

function headerFromSow(sow: Sow): HeaderDraft {
  return {
    name: sow.name,
    documentNo: sow.documentNo,
    rateCardId: sow.rateCardId,
    effectiveFrom: sow.effectiveFrom,
    effectiveTo: sow.effectiveTo,
    invoicingPauseFrom: sow.invoicingPauseFrom,
    invoicingPauseUntil: sow.invoicingPauseUntil,
    signedAt: sow.signedAt,
    signedBy: sow.signedBy,
  };
}

/**
 * Strip server-derived fields so a fetched Engagement becomes an
 * EngagementInput.
 *
 * `id` is CARRIED, not dropped: it is what a budget, a timesheet stamp and an
 * invoice line point at, so an amendment that sent the engagement back without
 * it would mint a new one and orphan all three.
 *
 * `fee` crosses the wire as a numeric string and is parsed once here. The
 * empty/NULL case is tested BEFORE parsing, not after: `Number('')` is 0, so
 * a null fee would otherwise pass the finite check as a real fee of zero and
 * the next amendment would save it — turning "no fee recorded" into "this
 * retainer is free" on a money field, with no warning (G15).
 *
 * A row that reaches here with no fee is already outside what the shape CHECK
 * permits, so 0 is a placeholder either way; what matters is that it is not
 * mistaken for a figure someone agreed.
 */
function engagementToInput(e: Engagement): EngagementInput {
  const common = {
    id: e.id,
    name: e.name,
    description: e.description,
    invoiceStart: e.invoiceStart,
    invoiceEnd: e.invoiceEnd,
    billingCadence: e.billingCadence,
    consolidate: e.consolidate,
    manual: e.manual,
    activities: e.activities,
  };
  switch (e.billingModel) {
    case 'retainer': {
      const fee = e.fee == null || e.fee.trim() === '' ? Number.NaN : Number(e.fee);
      return {
        ...common,
        billingModel: 'retainer',
        fee: Number.isFinite(fee) ? fee : 0,
        discount: e.discount,
        overageRates: e.overageRates,
      };
    }
    case 'tm':
      return { ...common, billingModel: 'tm', rates: e.rates ?? [] };
    case 'metrics':
      return {
        ...common,
        billingModel: 'metrics',
        metricName: e.metricName ?? '',
        tiers: e.tiers ?? [],
      };
  }
}

function DetailsTable({ sow, rateCards }: { sow: Sow; rateCards: Ratecard[] }) {
  const rateCard = sow.rateCardId ? rateCards.find((c) => c.id === sow.rateCardId) : undefined;
  const rows: [string, React.ReactNode][] = [
    ['Name', sow.name],
    ['Document #', sow.documentNo ?? <Muted>—</Muted>],
    ['Rate card', rateCard ? rateCardLabel(rateCard) : <Muted>—</Muted>],
    ['Version', `v${sow.version}`],
    ['Effective from', sow.effectiveFrom],
    ['Effective to', sow.effectiveTo ?? <Muted>open</Muted>],
    ['Invoicing pause', describeSowPause(sow) ?? <Muted>Not paused</Muted>],
    ['Created', new Date(sow.createdAt).toLocaleString()],
  ];
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {rows.map(([label, value]) => (
        <DetailRow key={label} label={label}>
          {value}
        </DetailRow>
      ))}
    </dl>
  );
}

/**
 * Editable counterpart of `DetailsTable`, shown when the user is in amend
 * mode. The two render in the same grid so toggling between them doesn't
 * jolt the page layout. Version and created timestamps stay read-only —
 * they can't be amended from here.
 */
function DetailsEditor({
  draft,
  onChange,
  onRateCardChange,
  disabled,
  sow,
  rateCards,
}: {
  draft: HeaderDraft;
  onChange: (next: HeaderDraft) => void;
  onRateCardChange: (rateCardId: string | null) => void;
  disabled?: boolean;
  sow: Sow;
  rateCards: Ratecard[];
}) {
  const set = <K extends keyof HeaderDraft>(key: K, value: HeaderDraft[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      <DetailRow label="Name">
        <Input
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          disabled={disabled}
          className="h-8"
        />
      </DetailRow>
      <DetailRow label="Document #">
        <Input
          value={draft.documentNo ?? ''}
          onChange={(e) => set('documentNo', e.target.value || null)}
          placeholder="Optional"
          disabled={disabled}
          className="h-8"
        />
      </DetailRow>
      <DetailRow label="Rate card">
        <Select
          value={draft.rateCardId ?? NO_RATE_CARD}
          onValueChange={(v) => onRateCardChange(v === NO_RATE_CARD ? null : v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select a rate card" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_RATE_CARD}>None</SelectItem>
            {rateCards.map((rc) => (
              <SelectItem key={rc.id} value={rc.id}>
                {rateCardLabel(rc)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DetailRow>
      <DetailRow label="Version">{`v${sow.version}`}</DetailRow>
      <DetailRow label="Effective from">
        <DatePicker
          value={draft.effectiveFrom}
          onChange={(v) => set('effectiveFrom', v)}
          disabled={disabled}
        />
      </DetailRow>
      <DetailRow label="Effective to">
        <DatePicker
          value={draft.effectiveTo ?? ''}
          onChange={(v) => set('effectiveTo', v || null)}
          placeholder="Open"
          clearable
          disabled={disabled}
        />
      </DetailRow>
      <DetailRow label="Invoicing paused from">
        <DatePicker
          value={draft.invoicingPauseFrom ?? ''}
          onChange={(v) =>
            // Clearing the start clears the whole window — an end date with no
            // start describes no pause, and the server rejects the pair.
            onChange({
              ...draft,
              invoicingPauseFrom: v || null,
              invoicingPauseUntil: v ? draft.invoicingPauseUntil : null,
            })
          }
          placeholder="Not paused"
          clearable
          disabled={disabled}
        />
      </DetailRow>
      <DetailRow label="Invoicing paused until">
        <DatePicker
          value={draft.invoicingPauseUntil ?? ''}
          onChange={(v) => set('invoicingPauseUntil', v || null)}
          placeholder={draft.invoicingPauseFrom ? 'Indefinite' : 'Set a start date first'}
          clearable
          // Meaningless without a start date, and the server would reject it.
          disabled={disabled || !draft.invoicingPauseFrom}
        />
      </DetailRow>
      <DetailRow label="Created">{new Date(sow.createdAt).toLocaleString()}</DetailRow>
    </dl>
  );
}

function DetailsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {Array.from({ length: 8 }).map((_, i) => (
        <DetailRow key={`detail-skel-${i}`} label="">
          <Skeleton className="h-4 w-40" />
        </DetailRow>
      ))}
    </div>
  );
}

function SowCustomFieldsPanel({ sow }: { sow: Sow }) {
  return (
    <div className="space-y-2">
      <CustomFieldsViewer object="sow" values={sow.customFields ?? null} />
    </div>
  );
}

function VersionsTable({ versions, currentVersion }: { versions: Sow[]; currentVersion?: number }) {
  if (versions.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No versions yet.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Version</TableHead>
          <TableHead>Effective from</TableHead>
          <TableHead>Effective to</TableHead>
          <TableHead>Recorded</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((v) => {
          const isCurrent = v.version === currentVersion;
          return (
            <TableRow key={v.version}>
              <TableCell className="font-medium">
                v{v.version}
                {isCurrent ? (
                  <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                    in effect
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{v.effectiveFrom}</TableCell>
              <TableCell>{v.effectiveTo ?? <Muted>open</Muted>}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {new Date(v.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

/** Picker/label text for a rate card: its name plus its effective window. */
function rateCardLabel(rc: Pick<Ratecard, 'name' | 'effectiveFrom' | 'effectiveTo'>): string {
  return `${rc.name} (${rc.effectiveFrom} – ${rc.effectiveTo ?? 'open'})`;
}

/**
 * The engagement's invoicing pause, in words — or `null` when there is none.
 *
 * Mirrors the customer-level wording on the customer detail page, deliberately:
 * the two pauses stack, and phrasing them differently would invite reading one
 * as a different kind of thing from the other.
 */
function describeSowPause(sow: Sow): string | null {
  const { invoicingPauseFrom: from, invoicingPauseUntil: until } = sow;
  if (!from) return null;
  const today = localTodayIso();
  if (isInvoicingPausedOn(sow, today)) {
    return until ? `Paused since ${from}, resuming ${until}` : `Paused since ${from}`;
  }
  if (today < from) return until ? `Pauses ${from}, resuming ${until}` : `Pauses ${from}`;
  return `Paused ${from} – ${until}, resumed`;
}
